import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoginLogo from "../../assets/img/logo-large.png";
import Google from "../../assets/img/google.svg";
import Apple from "../../assets/img/apple.svg";
import CanvasService from '../../Services/UserService';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { Icon } from "../../Components/Landing/Icon";
import PasswordInput from "../../Components/PasswordInput";
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface FormRegistration {
  FirstName: string;
  LastName: string;
  Email: string;
  Password: string;
  ConfirmPassword: string;
}

const schema = yup.object().shape({
  FirstName: yup.string().required('First name is required'),
  LastName: yup.string().required('Last name is required'),
  Email: yup.string().email('Invalid email format').required('Email is required'),
  Password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  ConfirmPassword: yup
    .string()
    .oneOf([yup.ref('Password'),""], 'Passwords must match')
    .required('Please confirm your password'),
});

const Registration: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormRegistration>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormRegistration) => {
    const { FirstName, LastName, Email, Password } = data;

    const postData = {
      FirstName,
      LastName,
      Email,
      Password,
    };

    try {
      const isRegistered = await CanvasService.RegisterUser(postData);
      console.log(isRegistered);
      if (isRegistered.success === true) {
        toast.success(isRegistered.message, { position: 'top-center' });
        setTimeout(() => {
          navigate(process.env.REACT_APP_PublicPath + "/login", { replace: false });
        }, 2000); 
      } else {
        toast.error(isRegistered.message, { position: 'top-right' });
      }
    } catch (error) {
      toast.error('An error occurred during registration', { position: 'top-right' });
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-100 py-14">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-extrabold tracking-tight mb-2 text-left">Create an account</h2>
             <p className='text-sm mb-6 '> Kindly fill in your details below to create an account</p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="FirstName" className="block text-sm font-semibold text-gray-700">
              First Name
            </label>
            <input
              id="FirstName"
              {...register('FirstName')}
              className="mt-1 block w-full p-2 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Alex"
            />
            {errors.FirstName && <p className="text-red-500 text-xs">{errors.FirstName.message}</p>}
          </div>
          <div>
            <label htmlFor="LastName" className="block text-sm font-semibold text-gray-700">
              Last Name
            </label>
            <input
              id="LastName"
              {...register('LastName')}
              className="mt-1 block w-full p-2 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Morgan"
            />
            {errors.LastName && <p className="text-red-500 text-xs">{errors.LastName.message}</p>}
          </div>
          <div>
            <label htmlFor="Email" className="block text-sm font-semibold text-gray-700">
              Email
            </label>
            <input
              id="Email"
              type="Email"
              {...register('Email')}
              className="mt-1 block w-full p-2 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="you@example.com"
            />
            {errors.Email && <p className="text-red-500 text-xs">{errors.Email.message}</p>}
          </div>
          <div>
            <label htmlFor="Password" className="block text-sm font-semibold text-gray-700">
              Password
            </label>
            <PasswordInput
              id="password"
              {...register('Password')}
              className="mt-1 block w-full p-2 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="At least 6 characters"
            />
            {errors.Password && <p className="text-red-500 text-xs">{errors.Password.message}</p>}
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700">
              Confirm Password
            </label>
            <PasswordInput
              id="confirmPassword"
              {...register('ConfirmPassword')}
              className="mt-1 block w-full p-2 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Re-enter your password"
            />
            {errors.ConfirmPassword && (
              <p className="text-red-500 text-xs">{errors.ConfirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            Register <Icon name="arrow" className="h-4 w-4" />
          </button>
        </form>

        <div className="flex items-center mt-6 justify-center text-sm font-normal">
          Already have an account?
          <Link to={process.env.REACT_APP_PublicPath + "/login"} className="text-blue-700 hover:underline pl-1">
            Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Registration;
